import type { JSX } from 'react';

import { Wrench } from 'lucide-react';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

import type { AgentTestScenario, AgentTestTurn } from '../../model/scenario';
import ScenarioScopeBadge from './ScenarioScopeBadge';

interface ScenarioPreviewPanelProps {
  scenario: AgentTestScenario | null;
  subProjectNames: Record<number, string>;
}

const TurnBubble = ({ turn }: { turn: AgentTestTurn }): JSX.Element => {
  if (turn.role === 'tool') {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[85%] flex-col gap-1 rounded-lg bg-brand-vivid px-3 py-2 opacity-80">
          <span className="flex items-center gap-1.5 text-[0.7rem] font-medium text-white/80">
            <Wrench className="size-3" aria-hidden />
            Tool{turn.toolName ? ` · ${turn.toolName}` : ''}
          </span>
          <pre className="m-0 font-mono text-xs break-words whitespace-pre-wrap text-white">{turn.content}</pre>
        </div>
      </div>
    );
  }

  const isUser = turn.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'flex max-w-[85%] flex-col gap-0.5 rounded-lg px-3 py-2',
          isUser ? 'bg-bg-surface-alt' : 'bg-brand-vivid'
        )}
      >
        <span className={cn('text-[0.7rem] font-medium', isUser ? 'text-text-muted' : 'text-white/80')}>
          {isUser ? 'Cliente' : 'Agente'}
        </span>
        <p className={cn('m-0 text-sm break-words', isUser ? 'text-text-default' : 'text-white')}>{turn.content}</p>
      </div>
    </div>
  );
};

const ScenarioPreviewPanel = ({ scenario, subProjectNames }: ScenarioPreviewPanelProps): JSX.Element => {
  if (scenario === null) {
    return (
      <div
        className="flex h-full min-h-[280px] items-center justify-center rounded-xl border border-dashed
          border-border-default bg-bg-surface-alt px-6 py-14 text-center"
        role="status"
      >
        <p className="m-0 text-sm text-text-muted">Selecciona un escenario para previsualizarlo.</p>
      </div>
    );
  }

  const subProjectName =
    scenario.scope === 'subProject' && scenario.subProjectId !== null
      ? subProjectNames[scenario.subProjectId] ?? `#${scenario.subProjectId}`
      : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 rounded-xl border border-border-default bg-bg-surface p-5">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <h3 className="m-0 text-base font-semibold text-text-default">{scenario.name}</h3>
          <span
            className={cn(
              'inline-block shrink-0 rounded-full px-2 py-0.5 text-[0.7rem] font-medium',
              scenario.enabled ? 'bg-success/15 text-success' : 'border border-border-subtle text-text-muted'
            )}
          >
            {scenario.enabled ? 'Habilitado' : 'Deshabilitado'}
          </span>
        </div>

        {scenario.description?.trim() ? <p className="m-0 text-sm text-text-muted">{scenario.description}</p> : null}

        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div className="flex flex-col gap-1">
            <span className="text-[0.7rem] text-text-muted">Alcance</span>
            <ScenarioScopeBadge scope={scenario.scope} />
          </div>
          {scenario.tag.trim().length > 0 ? (
            <div className="flex flex-col gap-1">
              <span className="text-[0.7rem] text-text-muted">Categoría</span>
              <span className="inline-block w-fit rounded-full border border-border-subtle px-2 py-0.5 text-[0.7rem] text-text-muted">
                {scenario.tag}
              </span>
            </div>
          ) : null}
          {subProjectName !== null ? (
            <div className="flex flex-col gap-1">
              <span className="text-[0.7rem] text-text-muted">Proyecto</span>
              <span className="text-xs text-text-default">{subProjectName}</span>
            </div>
          ) : null}
        </div>
      </div>

      <Separator />

      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <p className="m-0 text-sm font-semibold text-text-default">Simulación de la conversación</p>
        <div
          className="flex min-h-0 flex-1 flex-col-reverse gap-2 overflow-y-auto rounded-lg border border-border-subtle
            bg-bg-surface-raised p-3"
        >
          {[...scenario.turns].reverse().map((turn, index) => (
            <TurnBubble key={index} turn={turn} />
          ))}
        </div>
      </div>

      <Separator />

      <div className="flex shrink-0 flex-col gap-2">
        <p className="m-0 text-sm font-semibold text-text-default">Comportamiento esperado</p>
        <div className="max-h-[20vh] overflow-y-auto rounded-lg border border-border-subtle bg-bg-surface-raised px-3 py-2">
          <p className="m-0 text-sm break-words text-text-muted">{scenario.expectedBehavior}</p>
        </div>
      </div>
    </div>
  );
};

export default ScenarioPreviewPanel;
